import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import useSEO from "@/lib/useSEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS = [
  {
    q: "What are the shirts actually made of?",
    a: "Four garment families, exact blanks published. Unisex heavyweight tees: genuine Comfort Colors 1717 (6.1 oz, 100% ring-spun, garment-dyed). Women's relaxed tees: Next Level NL3910 boyfriend cut (4.3 oz combed ring-spun cotton; Heather Grey is 90/10 cotton-poly). Fitted baby tees: Bella+Canvas 1010 (5.3 oz 1x1 micro-rib, 52% combed cotton / 48% polyester). Hoodies: Lane Seven LS19001 (10 oz / 340 gsm three-end fleece, 70% cotton / 30% recycled polyester, fleece-lined three-panel hood). We publish the exact blanks because you deserve to know what you're paying for.",
  },
  {
    q: "How does sizing run?",
    a: "Everything runs true to size. Unisex heavyweight tees go S to 4XL (relaxed cut — size up for the oversized look), hoodies S to 3XL, women's relaxed tees XS to 2XL (boyfriend cut, take your usual size), and fitted baby tees XS to 2XL (size up if you want it less fitted). Every product page carries the actual manufacturer size chart — no vanity guesses.",
  },
  {
    q: "When will my order ship?",
    a: "Every piece is printed to order. Production takes 2–7 business days, then tracked US shipping typically lands in 2–5 business days — so 4 to 12 business days door to door, the same estimate Stripe shows you at checkout. Shipping is $4.95 for the first item and $1.95 for each extra item — and on orders $65+ the first item ships free (extra items still add $1.95 each). Multi-item orders may arrive in separate tracked parcels. Every order is personally reviewed by a human before it prints; that review sits inside the production window, not on top of it. Made-to-order means a short wait — and zero waste.",
  },
  {
    q: "Can I cancel my order?",
    a: "Yes — and there's a built-in window. Nothing prints until a real person here personally reviews your order and releases it to the printer. Email yell@fleecetheregime.com with your order number during that window and we'll cancel and refund in full. Once it's printing, it's yours — it was literally made for you.",
  },
  {
    q: "How fast do you answer email?",
    a: "Within 24 hours — usually much faster. One inbox, one human, no ticket numbers that vanish into the void: yell@fleecetheregime.com.",
  },
  {
    q: "How do I track my order?",
    a: (
      <>
        Head to{" "}
        <Link to="/track" className="font-bold text-black underline underline-offset-2 hover:text-primary transition-colors">
          Track Your Order
        </Link>{" "}
        with your order number (starts with FTR-) and the email you ordered with. You'll see where it is — printing, on a truck, delivered — plus the carrier tracking number once it ships.
      </>
    ),
  },
  {
    q: "Why made to order?",
    a: "Because warehouses full of unsold merch are how good intentions become landfill. Your shirt doesn't exist until you ask for it. Slightly slower, dramatically less stupid.",
  },
  {
    q: "What's the return policy?",
    a: "Misprints or defects: free replacement or full refund within 30 days of delivery. To start, email yell@fleecetheregime.com with your order number and clear photos of the problem — usually the photos are enough and nothing gets mailed back; if we ever do need the garment, we cover the return shipping. Approved refunds return to your original payment method and typically appear within 5–10 business days. Wrong size? Email within 30 days and we review it case by case — every piece is printed to order, so swaps can't just go back on a shelf, and if a return is approved we'll tell you who covers postage before you ship anything.",
  },
  {
    q: "Is this a real store?",
    a: "Yes. Real shirts, real heavyweight cotton, printed and shipped by a professional print partner, payments handled securely by Stripe (cards, Apple Pay, and more). The jokes are fake-deep; the operation is real.",
  },
  {
    q: "Do you ship internationally?",
    a: "Right now we ship within the US only, always tracked. Shipping is $4.95 for the first item plus $1.95 per extra item; on orders $65+ the first item ships free. Production takes 2–7 business days, then US delivery typically lands 2–5 business days after that. The wider world comes soon.",
  },
  {
    q: "Who writes these?",
    a: "In-house, late at night, with strong feelings and stronger coffee. Every design punches up at the government and the fascists — never down at people. That's a house rule, not a marketing line.",
  },
  {
    q: "How do I wash it?",
    a: "Machine wash cold, inside out, hang dry if you want it to outlive the news cycle. The garment-dyed fabric gets better with age — like dissent.",
  },
  {
    q: "Can I suggest a slogan?",
    a: (
      <>
        Please do. The best ones come from customers. Email{" "}
        <a href="mailto:yell@fleecetheregime.com" className="font-bold text-black underline underline-offset-2 hover:text-primary transition-colors">
          yell@fleecetheregime.com
        </a>{" "}
        — if we print it, you'll get one free.
      </>
    ),
  },
];

export default function FAQ() {
  useSEO(
    "FAQ — Sizing, Shipping, Returns & Fabric | Fleece The Regime",
    "Answers on published fabric specs for every blank, sizing (unisex tees S–4XL, women's relaxed XS–2XL, hoodies S–3XL, baby tees XS–2XL), made-to-order shipping times, cancellations, and our 30-day defect replacement policy.",
    false,
    "/faq/"
  );
  return (
    <main data-testid="faq-page" className="pt-16 min-h-screen">
      <section className="px-6 sm:px-12 lg:px-20 pt-20 pb-14 border-b-2 border-black">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-6">Questions, answered</p>
        <h1 className="font-display uppercase text-6xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-tighter" data-testid="faq-heading">
          Frequently<br />Asked <span className="text-primary">Dissent.</span>
        </h1>
      </section>
      <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-4xl">
        <Reveal>
          <Accordion type="single" collapsible className="border-t-2 border-black" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b-2 border-black">
                <AccordionTrigger
                  data-testid={`faq-question-${i}`}
                  className="font-display uppercase text-lg sm:text-xl text-left hover:no-underline hover:text-primary transition-colors"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="font-mono text-sm leading-relaxed text-black/70 max-w-2xl">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>
    </main>
  );
}
